<script lang="ts">
	import { ButtonV2 } from '@Shelly/shelly-design-system';
	import { createEventDispatcher } from 'svelte';
	import { format, locale } from 'svelte-i18n';
	import { navigateToShop } from 'src/components/Lists/list-helpers';
	import PromoModalBanner from 'src/components/Promotions/molecules/PromoModalBanner.svelte';
	import PromoModal from 'src/components/Promotions/organisms/PromoModal.svelte';
	import { PROMOTIONS } from 'src/constants';
	import { save_profile } from 'src/helpers/profile_helpers';
	import { getPromoExpiryText } from 'src/helpers/promo_helpers';
	import { capFirstChar } from 'src/helpers/utilities';
	import { profile_data, user_data } from 'src/stores';

	export let showModal = false;

	const dispatch = createEventDispatcher();
	const supportedImageLocales = ['de', 'fr', 'it'];

	const getImageLocale = (isUsMarket: boolean, selectedLocale: string) => {
		if (isUsMarket) return 'default';
		return supportedImageLocales.includes(selectedLocale) ? selectedLocale : 'default';
	};

	const updateCounter = (counter: string) => {
		const bd26 = { ...$profile_data?.aep?.bd26 };
		bd26[counter] = (bd26[counter] || 0) + 1;
		save_profile('aep', { ...$profile_data.aep, bd26 });
	};

	const closeModal = (trackClose = true) => {
		if (!showModal) return;

		showModal = false;
		if (!$profile_data?.promotions?.shellyBirthday2026) {
			save_profile('promotions', {
				...$profile_data?.promotions,
				shellyBirthday2026: new Date().getTime(),
			});
		}
		if (trackClose) {
			updateCounter('closeOnPromotionModal');
		}
		dispatch('close');
	};

	const handleMaybeLater = () => {
		closeModal();
	};

	const handleOpenShop = () => {
		updateCounter('clickOnShopNowButton');
		closeModal(false);

		const localePath = ['de', 'fr', 'it'].includes($locale) ? `${$locale}/` : '';
		const baseUrl = $user_data?.timezone?.startsWith('America/')
			? 'https://us.shelly.com/'
			: `https://www.shelly.com/${localePath}`;
		navigateToShop(
			`${baseUrl}pages/shelly-birthday?utm_source=shellyapp&utm_medium=referral&utm_campaign=shelly_birthday_2026`,
		);
	};

	$: expiry = getPromoExpiryText(PROMOTIONS.SHELLY_BIRTHDAY_2026.END_DATE);
	$: expiryText = expiry ? $format(expiry.key, expiry.object) : '';
	$: isUsMarket = !!$user_data?.timezone?.startsWith('America/');
	$: image = `./images/Shelly_BD_8y_KV_Adaptations_for_App_${getImageLocale(isUsMarket, $locale)}.png`;
</script>

<PromoModal
	{showModal}
	title={$format('title_shelly_birthday_2026', { default: '8 years deserve big deals' })}
	closeAriaLabel={$format('button_close', { default: 'Close' })}
	on:close={() => closeModal()}
	backdropClose
>
	<PromoModalBanner
		{image}
		alt={$format('title_shelly_birthday_2026', { default: '8 years deserve big deals' })}
		activeBadgeLabel={$format('label_active', { default: 'Active' })}
		aspectRatio="1200 / 800"
		overlayPosition="top"
		{expiryText}
	/>

	<div class="content-section">
		<div class="content-text">
			<p class="content-paragraph text-bold">
				{$format(
					isUsMarket
						? 'text_shelly_birthday_2026_intro_us'
						: 'text_shelly_birthday_2026_intro',
					{
						default: isUsMarket
							? 'Celebrate with us and enjoy up to 50% off, free gifts, and more'
							: 'Celebrate with us and enjoy up to -50%, free gifts, and more',
					},
				)}
			</p>
			<p class="content-paragraph text-300">
				{$format('text_shelly_birthday_2026_thanks', {
					default:
						"For eight years, we've been helping people make their homes smarter. Thank you for making Shelly part of your journey. Here's what we've prepared for our birthday",
				})}
			</p>
			<p class="content-paragraph text-bold">
				{$format(
					isUsMarket
						? 'text_shelly_birthday_2026_discount_heading_us'
						: 'text_shelly_birthday_2026_discount_heading',
					{ default: isUsMarket ? 'Up to 50% off' : 'Up to -50%' },
				)}
			</p>
			<p class="content-paragraph text-300">
				{$format('text_shelly_birthday_2026_discount', {
					default: 'Enjoy birthday savings on selected Shelly products',
				})}
			</p>
			<p class="content-paragraph text-bold">
				{$format('text_shelly_birthday_2026_shipping_heading', {
					default: 'Free shipping',
				})}
			</p>
			<p class="content-paragraph text-300">
				{$format(
					isUsMarket
						? 'text_shelly_birthday_2026_shipping_us'
						: 'text_shelly_birthday_2026_shipping',
					{
						default: isUsMarket
							? 'Free shipping on orders over $50 in the US.'
							: 'Free shipping on orders over €150 across eligible EU countries*',
					},
				)}
			</p>
			<p class="content-paragraph text-bold">
				{$format('text_shelly_birthday_2026_gift_heading', { default: 'Free gift' })}
			</p>
			<p class="content-paragraph text-300 mb-0">
				{$format(
					isUsMarket ? 'text_shelly_birthday_2026_gift_us' : 'text_shelly_birthday_2026_gift',
					{
						default: isUsMarket
							? 'Receive a free gift with every order over $200.'
							: 'Receive a Shelly Hand Fan with every order over €200.',
					},
				)}
			</p>
		</div>

		<div class="disclaimer">
			<p class="disclaimer-paragraph text-bold">
				{$format('text_shelly_birthday_2026_disclaimer_heading', {
					default: 'DISCLAIMER:',
				}).toUpperCase()}
			</p>
			<p class="disclaimer-paragraph">
				{$format(
					isUsMarket
						? 'text_shelly_birthday_2026_disclaimer_shipping_us'
						: 'text_shelly_birthday_2026_disclaimer_shipping',
					{
						default: isUsMarket
							? 'FREE SHIPPING ABOVE $50.'
							: 'FREE SHIPPING ABOVE €150 FOR EU COUNTRIES*.',
					},
				).toUpperCase()}
			</p>
			{#if !isUsMarket}
				<p class="disclaimer-paragraph">
					{$format('text_shelly_birthday_2026_disclaimer_countries', {
						default:
							'*AUSTRIA, BELGIUM, BULGARIA (ABOVE €50), CROATIA, CZECHIA, DENMARK, ESTONIA, FINLAND, FRANCE, GERMANY (ABOVE €79), GREECE, HUNGARY, IRELAND, ITALY (ABOVE €79), LATVIA, LITHUANIA, LUXEMBOURG, MALTA, NETHERLANDS, POLAND (ABOVE 330 ZŁ), PORTUGAL, ROMANIA, SLOVAKIA, SLOVENIA, SPAIN, SWEDEN',
					}).toUpperCase()}
				</p>
			{/if}
		</div>
	</div>

	<svelte:fragment slot="footer">
		<div class="width-full d-flex justify-content-end gap-s">
			<ButtonV2
				variant="secondary"
				on:click={handleMaybeLater}
				class="min-width-md-m min-width-sm-auto width-md-auto width-sm-full text-nowrap"
			>
				{capFirstChar($format('button_maybe_later', { default: 'Maybe later' }))}
			</ButtonV2>
			<ButtonV2
				variant="primary"
				on:click={handleOpenShop}
				icon="fas fa-shop"
				class="min-width-md-m min-width-sm-auto width-md-auto width-sm-full text-nowrap"
			>
				{$format('button_shop_now', { default: 'Shop Now' })}
			</ButtonV2>
		</div>
	</svelte:fragment>
</PromoModal>

<style>
	.content-section {
		display: flex;
		flex-direction: column;
		gap: 12px;
		padding: 24px 24px 0;
	}

	.content-text {
		font-family: 'Inter', sans-serif;
		font-size: 16px;
		font-weight: 400;
		line-height: 19px;
		color: var(--color-white, #ffffff);
	}

	.content-paragraph {
		margin: 0 0 10px;
	}

	.disclaimer {
		font-family: 'Inter', sans-serif;
		font-size: 8px;
		font-weight: 500;
		line-height: 1;
		letter-spacing: 0.48px;
		color: var(--color-white, #ffffff);
		text-transform: uppercase;
	}

	.disclaimer-paragraph {
		margin: 0;
	}

	@media (max-width: 639px) {
		.content-section {
			padding: 16px 16px 0;
		}
	}
</style>
